import { useState, useEffect } from 'react';

import useTextKeyLimit from '../../pages/hooks/useTextKeyLimit';
import Validations, { InputPatterns } from '../../utils/validations';
import countryCodeData from './countryCodeData';

const hardcodedDefault = {
    code: '61',
    index: 2,
    country: 'au',
};

function resolveDefaultCountry (geoCountry) {
    if (!geoCountry) return hardcodedDefault;
    const isoCode = geoCountry.toLowerCase();
    const idx = countryCodeData.findIndex(c => c.iso2 === isoCode);
    if (idx === -1) return hardcodedDefault;
    const found = countryCodeData[idx];
    return { code: found.code, index: idx, country: found.iso2 };
}

function useCountryCodeUIState (props) {
    const {
        value,
        geoCountry,
    } = props;

    const defaultCountry = resolveDefaultCountry(geoCountry);

    const [countryCode, setCountryCode] = useState(value.countryCode || defaultCountry.code);
    const [country, setCountry] = useState(value.country?.toLowerCase() || defaultCountry.country);

    const [openList, setOpenList] = useState(false);
    const [listAnchor, setListAnchor] = useState(null);
    const [codeIndex, setCodeIndex] = useState(value.codeIndex || defaultCountry.index);
    const [phoneNumber, setPhoneNumber] = useState(value.phoneNumber || '');

    const {
        onKeyDown: onPhoneKeyDown,
    } = useTextKeyLimit({
        includedPattern: InputPatterns.numberOnly,
        charLimit: Validations.phone.maxLength,
        pasteEnabled: false,
    });

    function syncFromFullValue (v) {
        setCountryCode(v.countryCode);
        setPhoneNumber(v.phoneNumber);
        setCountry(v.country);
        setCodeIndex(v.codeIndex);
    }

    function syncFromCountryISO (v) {
        const isoCode = v.country.toLowerCase();
        const idx = countryCodeData.findIndex(c => c.iso2 === isoCode);
        if (idx !== -1) {
            const found = countryCodeData[idx];
            setCountryCode(found.code);
            setCountry(found.iso2);
            setCodeIndex(idx);
        }
        setPhoneNumber(v.phoneNumber || '');
    }

    useEffect(() => {
        if (value.codeIndex !== undefined) {
            syncFromFullValue(value);
        } else if (value.country) {
            syncFromCountryISO(value);
        }
    }, [value]);

    // Apply geo-based country default when location data arrives and no explicit value was set
    function applyGeoDefault () {
        if (!geoCountry) return;

        if (value.countryCode || value.codeIndex !== undefined || value.country) return;

        const resolved = resolveDefaultCountry(geoCountry);
        if (resolved === hardcodedDefault && geoCountry) return;
        
        setCountryCode(resolved.code);
        setCountry(resolved.country);
        setCodeIndex(resolved.index);
    }

    useEffect(() => {
        applyGeoDefault();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geoCountry, value.countryCode, value.codeIndex, value.country]);

    function openCountryCodeList (p) {
        setListAnchor(p.anchor);
        setOpenList(true);
    }
    
    function closeCountryCodeList () {
        setOpenList(false);
    }

    function selectCountryCode (p) {
        setCountry(p.data.iso2);
        setCodeIndex(p.index);
        setCountryCode(p.data.code);
    }

    return {
        countryCode,
        openList,
        listAnchor,
        openCountryCodeList,
        closeCountryCodeList,
        selectCountryCode,
        codeIndex,
        country,
        onPhoneKeyDown,

        phoneNumber,
        setPhoneNumber,
    };
}

export default useCountryCodeUIState;
